import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Clock, ArrowRight, Send, Loader2, CheckCircle2, Users, Building2 } from "lucide-react";
import { toast } from "sonner";
import { sendInquiry } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const VARIANTS = {
  parents: {
    icon: Users,
    eyebrow: "For Parents & Families",
    title: "We'd Love to Hear From Your Den.",
    sub: "A question about an order, the Activity Pack, or which fable to start with? Write to us — a real human (not a jackal) replies.",
    topics: ["Book Orders & Delivery", "Activity Pack", "Feedback & Story Ideas", "Something Else"],
    audience: "parents",
    switchLabel: "Publisher, retailer or press?",
    switchCta: "Visit the Business Desk",
    switchTo: "/publishers",
    testPrefix: "contact-parents",
  },
  publishers: {
    icon: Building2,
    eyebrow: "For Publishers & Business",
    title: "Let's Bring the Banyan to Your Market.",
    sub: "Foreign rights, distribution, licensing, or press — Banyan Tree Press is building a 50-book world and welcomes serious partners.",
    topics: ["Translation & Foreign Rights", "Distribution & Wholesale", "Film, TV & Audio Licensing", "Press & Media", "Other Business"],
    audience: "publishers",
    switchLabel: "Parent or young reader's family?",
    switchCta: "Visit the Family Desk",
    switchTo: "/contact",
    testPrefix: "contact-publishers",
  },
};

export default function ContactPage({ variant = "parents" }) {
  const v = VARIANTS[variant];
  const Icon = v.icon;
  const [form, setForm] = useState({ name: "", company: "", email: "", topic: v.topics[0], message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (!form.name.trim()) er.name = "Please tell us your name";
    if (!EMAIL_RE.test(form.email)) er.email = "Please enter a valid email address";
    if (!form.message.trim()) er.message = "Please write a few lines";
    if (variant === "publishers" && !form.company.trim()) er.company = "Please add your imprint or company";
    setErrors(er);
    if (Object.keys(er).length) return;
    setStatus("loading");
    try {
      await sendInquiry({
        name: form.name,
        email: form.email,
        inquiry_type: form.topic,
        message: form.message,
        company: variant === "publishers" ? form.company : undefined,
        audience: v.audience,
      });
      setStatus("done");
      toast.success("Message sent — we'll reply within two business days.");
    } catch {
      setStatus("idle");
      toast.error("Could not send your message. Please try again.");
    }
  };

  const inputCls =
    "w-full rounded-sm border border-cream/20 bg-emerald-deep/70 px-4 py-3.5 text-cream placeholder:text-cream/35 focus:border-gold focus:outline-none";
  const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-gold/85";

  return (
    <div data-testid={`${v.testPrefix}-page`}>
      <section className="grain relative overflow-hidden bg-emerald-ink pb-24 pt-40 lg:pb-32 lg:pt-48">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(900px 520px at 85% 0%, rgba(19,78,61,0.85), transparent 65%), radial-gradient(700px 420px at 10% 100%, rgba(212,175,55,0.1), transparent 60%)",
          }}
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-gold"
            data-testid={`${v.testPrefix}-eyebrow`}
          >
            <span className="h-px w-10 bg-gold/70" aria-hidden="true" />
            {v.eyebrow}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.12, ease: EASE }}
            className="mt-6 max-w-3xl font-serif text-4xl font-bold leading-[1.08] tracking-tight text-cream sm:text-5xl lg:text-6xl"
            data-testid={`${v.testPrefix}-title`}
          >
            {v.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.24, ease: EASE }}
            className="mt-6 max-w-2xl text-base leading-relaxed text-cream/70 sm:text-lg"
          >
            {v.sub}
          </motion.p>

          <div className="mt-16 grid gap-10 lg:grid-cols-[1.2fr_0.8fr]">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
              className="border border-gold/25 bg-emerald-deep/80 p-8 shadow-lift sm:p-10"
            >
              <AnimatePresence mode="wait">
                {status === "done" ? (
                  <motion.div
                    key="done"
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: EASE }}
                    className="py-10 text-center"
                    data-testid={`${v.testPrefix}-success`}
                  >
                    <CheckCircle2 className="mx-auto h-14 w-14 text-gold" aria-hidden="true" />
                    <h2 className="mt-6 font-serif text-3xl font-semibold text-cream">
                      Your message is beneath the banyan.
                    </h2>
                    <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-cream/70">
                      Thank you, {form.name.split(" ")[0]}. We read every note and reply within
                      two business days from Sales@visitbanyantree.com.
                    </p>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    onSubmit={submit}
                    noValidate
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="space-y-5"
                    data-testid={`${v.testPrefix}-form`}
                  >
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <label htmlFor={`${v.testPrefix}-name`} className={labelCls}>Your Name</label>
                        <input
                          id={`${v.testPrefix}-name`}
                          type="text"
                          value={form.name}
                          onChange={set("name")}
                          data-testid={`${v.testPrefix}-name-input`}
                          aria-invalid={!!errors.name}
                          placeholder={variant === "parents" ? "Maya Sharma" : "Alexandra Braun"}
                          className={inputCls}
                        />
                        {errors.name && <p className="mt-1.5 text-xs text-gold-amber" role="alert">{errors.name}</p>}
                      </div>
                      <div>
                        <label htmlFor={`${v.testPrefix}-email`} className={labelCls}>Email Address</label>
                        <input
                          id={`${v.testPrefix}-email`}
                          type="email"
                          value={form.email}
                          onChange={set("email")}
                          data-testid={`${v.testPrefix}-email-input`}
                          aria-invalid={!!errors.email}
                          placeholder="you@example.com"
                          className={inputCls}
                        />
                        {errors.email && <p className="mt-1.5 text-xs text-gold-amber" role="alert">{errors.email}</p>}
                      </div>
                    </div>

                    {variant === "publishers" && (
                      <div>
                        <label htmlFor={`${v.testPrefix}-company`} className={labelCls}>Imprint / Company</label>
                        <input
                          id={`${v.testPrefix}-company`}
                          type="text"
                          value={form.company}
                          onChange={set("company")}
                          data-testid={`${v.testPrefix}-company-input`}
                          aria-invalid={!!errors.company}
                          placeholder="e.g. Riverside Books Ltd."
                          className={inputCls}
                        />
                        {errors.company && <p className="mt-1.5 text-xs text-gold-amber" role="alert">{errors.company}</p>}
                      </div>
                    )}

                    <div>
                      <label htmlFor={`${v.testPrefix}-topic`} className={labelCls}>Topic</label>
                      <select
                        id={`${v.testPrefix}-topic`}
                        value={form.topic}
                        onChange={set("topic")}
                        data-testid={`${v.testPrefix}-topic-select`}
                        className={inputCls}
                      >
                        {v.topics.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor={`${v.testPrefix}-message`} className={labelCls}>Message</label>
                      <textarea
                        id={`${v.testPrefix}-message`}
                        rows={6}
                        value={form.message}
                        onChange={set("message")}
                        data-testid={`${v.testPrefix}-message-input`}
                        aria-invalid={!!errors.message}
                        placeholder={variant === "parents"
                          ? "Tell us how we can help your little reader…"
                          : "Tell us about your market, territory, or project…"}
                        className={`${inputCls} resize-none`}
                      />
                      {errors.message && <p className="mt-1.5 text-xs text-gold-amber" role="alert">{errors.message}</p>}
                    </div>

                    <button
                      type="submit"
                      disabled={status === "loading"}
                      data-testid={`${v.testPrefix}-submit-btn`}
                      className="flex w-full items-center justify-center gap-2.5 rounded-full bg-gold px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-emerald-ink transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-glow disabled:opacity-60 sm:w-auto"
                    >
                      {status === "loading" ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      ) : (
                        <Send className="h-4 w-4" aria-hidden="true" />
                      )}
                      Send Message
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </motion.div>

            <motion.aside
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: EASE }}
              className="space-y-6"
            >
              <div className="border border-gold/25 bg-emerald-deep/60 p-7">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/35 text-gold">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <h2 className="mt-5 font-serif text-2xl font-semibold text-cream">Write to us directly</h2>
                <a
                  href="mailto:Sales@visitbanyantree.com"
                  data-testid={`${v.testPrefix}-email-link`}
                  className="mt-3 flex items-center gap-2.5 text-base font-medium text-gold underline-offset-4 transition-colors hover:text-gold-amber hover:underline"
                >
                  <Mail className="h-4 w-4 shrink-0" aria-hidden="true" />
                  Sales@visitbanyantree.com
                </a>
                <p className="mt-4 flex items-center gap-2 text-sm text-cream/60">
                  <Clock className="h-4 w-4 shrink-0 text-gold/70" aria-hidden="true" />
                  Replies within two business days
                </p>
              </div>

              <Link
                to={v.switchTo}
                data-testid={`${v.testPrefix}-switch-link`}
                className="group flex items-center justify-between border border-cream/15 bg-emerald/50 p-7 transition-colors duration-300 hover:border-gold/40"
              >
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-cream/55">
                    {v.switchLabel}
                  </p>
                  <p className="mt-2 font-serif text-xl font-semibold text-cream">{v.switchCta}</p>
                </div>
                <ArrowRight className="h-5 w-5 text-gold transition-transform duration-300 group-hover:translate-x-1.5" aria-hidden="true" />
              </Link>
            </motion.aside>
          </div>
        </div>
      </section>
    </div>
  );
}
